/**
 * Script: DonationPickupUploadService.js
 * Caminho: src/services/DonationPickupUploadService.js
 * Descricao: Service frontend para Auth anonimo, sessao temporaria,
 * conversao WebP e upload temporario de fotos de Abholspenden.
 *
 * Relacao com o Projeto:
 *  - Suporta a pagina publica /spenden.
 *  - Controla a sessao em spendenUploadsTemp/{sessionId}.
 *  - Prepara imagens recortadas para WebP 1000x1000.
 *
 * Entradas (Parametros):
 *  - Sessao temporaria, Canvas/Blob/File de imagem e metadados.
 *
 * Saidas:
 *  - Usuario anonimo, sessionId, paths temporarios e UploadResult.
 *
 * Exports:
 *  - Limites, Auth anonimo, sessao temporaria, preparo WebP,
 *    paths temporarios, upload individual e limpeza da sessao.
 *
 * Dependencias:
 *  - Utilitarios internos: firebaseConfig.
 *  - Componentes Vue: SpendenFormularView.vue.
 *  - Backend (Cloud Functions/API): validacao final futura.
 *  - Bibliotecas externas (NPM): Firebase Web SDK modular e
 *    browser-image-compression.
 */

import { auth, db, storage } from "@/firebaseConfig";
import imageCompression from "browser-image-compression";
import { signInAnonymously, signOut } from "firebase/auth";
import { doc, serverTimestamp, setDoc, Timestamp } from "firebase/firestore";
import { deleteObject, ref as storageRef, uploadBytes } from "firebase/storage";

const SESSION_STORAGE_KEY = "donationPickup.tempSession";

export const DONATION_PICKUP_UPLOAD_LIMITS = Object.freeze({
  TEMP_SESSION_TTL_MS: 24 * 60 * 60 * 1000,
  TEMP_UPLOAD_MAX_BYTES: 5 * 1024 * 1024,
  TEMP_UPLOAD_CONTENT_TYPE: "image/webp",
  TARGET_IMAGE_SIZE: 1000,
  COMPRESSION_MAX_SIZE_MB: 1,
});

function getCryptoApi() {
  if (typeof crypto !== "undefined") return crypto;
  return null;
}

function createFallbackId() {
  const cryptoApi = getCryptoApi();
  if (cryptoApi?.getRandomValues) {
    const values = new Uint32Array(4);
    cryptoApi.getRandomValues(values);
    return Array.from(values, (value) => value.toString(16)).join("");
  }
  return `${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
}

function createSessionId() {
  const cryptoApi = getCryptoApi();
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  return `spenden_${createFallbackId()}`;
}

function sanitizePathSegment(value, fallback) {
  const sanitized = String(value || "")
    .trim()
    .replace(/[^A-Za-z0-9_-]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  return sanitized || fallback;
}

function getSessionStorage() {
  if (typeof window === "undefined") return null;
  return window.sessionStorage || null;
}

function loadStoredSession(authUid) {
  try {
    const rawValue = getSessionStorage()?.getItem(SESSION_STORAGE_KEY);
    if (!rawValue) return null;
    const session = JSON.parse(rawValue);
    if (session?.authUid !== authUid) return null;
    if (!session?.sessionId) return null;
    if (session?.expiresAtMs && session.expiresAtMs <= Date.now()) return null;
    return session;
  } catch (_) {
    return null;
  }
}

function saveStoredSession(session) {
  try {
    getSessionStorage()?.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  } catch (_) {
    /* armazenamento local indisponivel nao bloqueia a sessao */
  }
}

function clearStoredSession(session) {
  try {
    const sessionStorage = getSessionStorage();
    const storedSession = JSON.parse(
      sessionStorage?.getItem(SESSION_STORAGE_KEY) || "null"
    );
    if (!session || storedSession?.sessionId === session.sessionId) {
      sessionStorage?.removeItem(SESSION_STORAGE_KEY);
    }
  } catch (_) {
    getSessionStorage()?.removeItem(SESSION_STORAGE_KEY);
  }
}

function createExpiresAt(nowMs = Date.now()) {
  return Timestamp.fromDate(
    new Date(nowMs + DONATION_PICKUP_UPLOAD_LIMITS.TEMP_SESSION_TTL_MS)
  );
}

function normalizePhotoPaths(photoPaths = []) {
  return Array.from(
    new Set(
      (Array.isArray(photoPaths) ? photoPaths : [])
        .map((path) => String(path || "").trim())
        .filter((path) => path.startsWith("donation-pickups/temp/"))
    )
  );
}

function requireUploadSession(session) {
  if (!session?.authUid || !session?.sessionId) {
    throw new Error("invalid-donation-pickup-session");
  }
}

function buildDonationPickupTempItemPrefix(session, itemId) {
  requireUploadSession(session);
  return [
    "donation-pickups",
    "temp",
    sanitizePathSegment(session.authUid, "anonymous"),
    sanitizePathSegment(session.sessionId, "session"),
    sanitizePathSegment(itemId, "item_001"),
    "",
  ].join("/");
}

function isMissingStorageObject(error) {
  return String(error?.code || "") === "storage/object-not-found";
}

async function deleteDonationPickupTempPaths(paths, storageBucket) {
  const results = await Promise.allSettled(
    paths.map((path) => deleteObject(storageRef(storageBucket, path)))
  );
  return results.some(
    (result) =>
      result.status === "rejected" && !isMissingStorageObject(result.reason)
  );
}

function normalizeWebpFileName(fileName = "photo.webp") {
  const safeName = sanitizePathSegment(
    String(fileName).replace(/\.[^.]+$/, ""),
    "photo"
  );
  return `${safeName}.webp`;
}

function canvasToBlob(canvas, contentType, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }
        reject(new Error("invalid-donation-pickup-canvas"));
      },
      contentType,
      quality
    );
  });
}

function blobToFile(blob, fileName) {
  return new File([blob], normalizeWebpFileName(fileName), {
    type: DONATION_PICKUP_UPLOAD_LIMITS.TEMP_UPLOAD_CONTENT_TYPE,
    lastModified: Date.now(),
  });
}

export async function ensureDonationPickupAnonymousAuth(firebaseAuth = auth) {
  if (firebaseAuth?.currentUser?.isAnonymous) {
    return firebaseAuth.currentUser;
  }
  if (firebaseAuth?.currentUser && !firebaseAuth.currentUser.isAnonymous) {
    await signOut(firebaseAuth);
  }
  const credential = await signInAnonymously(firebaseAuth);
  return credential.user;
}

export function buildDonationPickupTempPhotoPath({
  authUid,
  sessionId,
  itemId,
  photoId,
}) {
  const safeAuthUid = sanitizePathSegment(authUid, "anonymous");
  const safeSessionId = sanitizePathSegment(sessionId, "session");
  const safeItemId = sanitizePathSegment(itemId, "item_001");
  const safePhotoId = sanitizePathSegment(photoId, "photo_001");
  const fileName = safePhotoId.endsWith(".webp")
    ? safePhotoId
    : `${safePhotoId}.webp`;

  return [
    "donation-pickups",
    "temp",
    safeAuthUid,
    safeSessionId,
    safeItemId,
    fileName,
  ].join("/");
}

export async function updateDonationPickupUploadSession({
  sessionId,
  authUid,
  photoPaths = [],
  firestoreDb = db,
  nowMs = Date.now(),
}) {
  const safePhotoPaths = normalizePhotoPaths(photoPaths);
  const expiresAt = createExpiresAt(nowMs);

  await setDoc(
    doc(firestoreDb, "spendenUploadsTemp", sessionId),
    {
      sessionId,
      authUid,
      updatedAt: serverTimestamp(),
      expiresAt,
      finalized: false,
      protocol: null,
      photoPaths: safePhotoPaths,
    },
    { merge: true }
  );

  return {
    sessionId,
    authUid,
    expiresAt,
    expiresAtMs: expiresAt.toMillis(),
    photoPaths: safePhotoPaths,
  };
}

export function forgetDonationPickupUploadSession(session = null) {
  clearStoredSession(session);
}

export async function removeDonationPickupItemPhotos({
  session,
  itemId,
  firestoreDb = db,
  storageBucket = storage,
}) {
  const itemPrefix = buildDonationPickupTempItemPrefix(session, itemId);
  const photoPaths = normalizePhotoPaths(session.photoPaths);
  const removedPaths = photoPaths.filter((path) => path.startsWith(itemPrefix));

  if (!removedPaths.length) {
    return { ...session, photoPaths, cleanupPending: false };
  }

  const removedSet = new Set(removedPaths);
  const updatedSession = await updateDonationPickupUploadSession({
    sessionId: session.sessionId,
    authUid: session.authUid,
    photoPaths: photoPaths.filter((path) => !removedSet.has(path)),
    firestoreDb,
  });
  saveStoredSession(updatedSession);

  const cleanupPending = await deleteDonationPickupTempPaths(
    removedPaths,
    storageBucket
  );
  return { ...updatedSession, cleanupPending };
}

export async function cancelDonationPickupUploadSession({
  session,
  firestoreDb = db,
  storageBucket = storage,
}) {
  requireUploadSession(session);
  clearStoredSession(session);
  const photoPaths = normalizePhotoPaths(session.photoPaths);

  await setDoc(
    doc(firestoreDb, "spendenUploadsTemp", session.sessionId),
    {
      updatedAt: serverTimestamp(),
      finalized: true,
      cancelled: true,
      cancelledAt: serverTimestamp(),
      protocol: null,
      photoPaths: [],
    },
    { merge: true }
  );

  const cleanupPending = await deleteDonationPickupTempPaths(
    photoPaths,
    storageBucket
  );
  return {
    ...session,
    finalized: true,
    cancelled: true,
    photoPaths: [],
    cleanupPending,
  };
}

export async function prepareDonationPickupPhoto({
  file,
  canvas = null,
  fileName = file?.name || "photo.webp",
}) {
  if (!file?.type?.startsWith("image/")) {
    throw new Error("invalid-donation-pickup-photo-type");
  }

  const sourceFile = canvas
    ? blobToFile(
        await canvasToBlob(
          canvas,
          DONATION_PICKUP_UPLOAD_LIMITS.TEMP_UPLOAD_CONTENT_TYPE,
          0.86
        ),
        fileName
      )
    : file;

  const compressedFile = await imageCompression(sourceFile, {
    maxSizeMB: DONATION_PICKUP_UPLOAD_LIMITS.COMPRESSION_MAX_SIZE_MB,
    maxWidthOrHeight: DONATION_PICKUP_UPLOAD_LIMITS.TARGET_IMAGE_SIZE,
    useWebWorker: true,
    fileType: DONATION_PICKUP_UPLOAD_LIMITS.TEMP_UPLOAD_CONTENT_TYPE,
    initialQuality: 0.86,
  });

  return blobToFile(compressedFile, fileName);
}

export async function createDonationPickupUploadSession({
  firebaseAuth = auth,
  firestoreDb = db,
  nowMs = Date.now(),
} = {}) {
  const timeoutPromise = new Promise((_, reject) =>
    setTimeout(
      () => reject(new Error("donation-pickup-session-timeout")),
      20000
    )
  );

  const sessionPromise = (async () => {
    const user = await ensureDonationPickupAnonymousAuth(firebaseAuth);
    const storedSession = loadStoredSession(user.uid);
    const sessionId = storedSession?.sessionId || createSessionId();
    const expiresAt = createExpiresAt(nowMs);

    await setDoc(
      doc(firestoreDb, "spendenUploadsTemp", sessionId),
      {
        sessionId,
        authUid: user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        expiresAt,
        finalized: false,
        protocol: null,
        photoPaths: storedSession?.photoPaths || [],
      },
      { merge: true }
    );

    const session = {
      sessionId,
      authUid: user.uid,
      expiresAt,
      expiresAtMs: expiresAt.toMillis(),
      photoPaths: storedSession?.photoPaths || [],
    };
    saveStoredSession(session);
    return session;
  })();

  return Promise.race([sessionPromise, timeoutPromise]);
}

export async function uploadDonationPickupPhoto({
  session,
  itemId,
  photoId,
  file,
  canvas = null,
  storageBucket = storage,
  firestoreDb = db,
}) {
  requireUploadSession(session);
  const preparedFile = await prepareDonationPickupPhoto({
    file,
    canvas,
    fileName: `${photoId}.webp`,
  });
  if (preparedFile.size > DONATION_PICKUP_UPLOAD_LIMITS.TEMP_UPLOAD_MAX_BYTES) {
    throw new Error("invalid-donation-pickup-photo-size");
  }

  const storagePath = buildDonationPickupTempPhotoPath({
    authUid: session.authUid,
    sessionId: session.sessionId,
    itemId,
    photoId,
  });

  await uploadBytes(storageRef(storageBucket, storagePath), preparedFile, {
    contentType: DONATION_PICKUP_UPLOAD_LIMITS.TEMP_UPLOAD_CONTENT_TYPE,
    customMetadata: {
      sessionId: session.sessionId,
      itemId: sanitizePathSegment(itemId, "item_001"),
    },
  });

  let updatedSession;
  try {
    updatedSession = await updateDonationPickupUploadSession({
      sessionId: session.sessionId,
      authUid: session.authUid,
      photoPaths: [...(session.photoPaths || []), storagePath],
      firestoreDb,
    });
  } catch (error) {
    try {
      await deleteObject(storageRef(storageBucket, storagePath));
    } catch (_) {
      /* o job de TTL remove eventual objeto orfao pelo prefixo da sessao */
    }
    throw error;
  }
  saveStoredSession(updatedSession);

  return { storagePath, file: preparedFile, session: updatedSession };
}

export async function createOperatorDonationPickupUploadSession({
  firebaseAuth = auth,
  firestoreDb = db,
  nowMs = Date.now(),
} = {}) {
  const timeoutPromise = new Promise((_, reject) =>
    setTimeout(
      () => reject(new Error("donation-pickup-session-timeout")),
      20000
    )
  );

  const sessionPromise = (async () => {
    const user = firebaseAuth?.currentUser;
    if (!user || user.isAnonymous) {
      throw new Error("operator-auth-required");
    }
    const sessionId = createSessionId();
    const expiresAt = createExpiresAt(nowMs);

    await setDoc(
      doc(firestoreDb, "spendenUploadsTemp", sessionId),
      {
        sessionId,
        authUid: user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        expiresAt,
        finalized: false,
        protocol: null,
        photoPaths: [],
      },
      { merge: true }
    );

    const session = {
      sessionId,
      authUid: user.uid,
      expiresAt,
      expiresAtMs: expiresAt.toMillis(),
      photoPaths: [],
    };
    return session;
  })();

  return Promise.race([sessionPromise, timeoutPromise]);
}

export async function finalizeDonationPickupUploads() {}
